import { useRef } from 'react';
import { useCustomizeMode } from '@/features/experiences/components/CustomizeMode';
import { SCHEDULE_CATEGORIES_TAB } from '@/features/experiences/components/CustomizeMode/customizeOptions';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import type { PageExperience } from '@/features/experiences/types';
import {
  toggleFilterId,
  useExperienceEventFilters,
} from '@/features/planner/hooks/useExperienceEventFilters';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import {
  Button,
  Flex,
  Icon,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Tabs,
  TabPanels,
  useDisclosure,
  VStack,
} from '@/ui';
import { ActiveFiltersPill } from './ActiveFiltersPill';
import {
  AVAILABLE_HEIGHT_VAR,
  availableHeightModifiers,
} from './availableHeightModifier';
import { CategoryFilterRow } from './CategoryFilterRow';
import { CountBadge } from './CountBadge';
import { FilterEmptyState } from './FilterEmptyState';
import { FilterLabelList } from './FilterLabelList';
import { FilterTab } from './FilterTab';
import { FilterTabList } from './FilterTabList';
import { FilterTabPanel } from './FilterTabPanel';
import { ManageLink } from './ManageLink';
import { SourceFilterRow } from './SourceFilterRow';

type Props = {
  experience: PageExperience;
};

export const EventFilter = ({ experience }: Props) => {
  const {
    isFilteringEnabled,
    activeFeeds,
    filterIds,
    filteredCategories,
    filteredLabels,
    filteredWhoLabels,
    filteredSources,
    hasAppliedFilters,
    setFilters,
    clearFilters,
  } = useExperienceEventFilters(experience);
  const { readOnly } = usePageExperienceContext();
  const { setActiveControl, setActiveControlTab } = useCustomizeMode();
  const {
    isOpen,
    onOpen: openPopover,
    onClose: closePopover,
    onToggle: togglePopover,
  } = useDisclosure();
  const isMobileBreakpoint = useIsMobileBreakpoint();
  const initialFocusRef = useRef<HTMLElement | null>(null);

  if (!isFilteringEnabled) {
    return null;
  }

  const filtersCount =
    filteredCategories.length +
    filteredWhoLabels.length +
    filteredLabels.length +
    filteredSources.length;

  const handleCategoryToggle = (categoryId: string) =>
    setFilters({
      ...filterIds,
      categories: toggleFilterId(filterIds.categories, categoryId),
    });

  const handleWhoLabelToggle = (labelId: string) =>
    setFilters({
      ...filterIds,
      whoLabels: toggleFilterId(filterIds.whoLabels, labelId),
    });

  const handleLabelToggle = (labelId: string) =>
    setFilters({
      ...filterIds,
      labels: toggleFilterId(filterIds.labels, labelId),
    });

  const handleSourceToggle = (sourceId: string) =>
    setFilters({
      ...filterIds,
      sources: toggleFilterId(filterIds.sources, sourceId),
    });

  // Categories, who labels, and labels are all edited from the one Categories &
  // Labels section of Schedule settings; sources have a panel of their own.
  const manageCategoriesAndLabels = () => {
    closePopover();
    setActiveControlTab('schedule', SCHEDULE_CATEGORIES_TAB);
  };

  const manageSources = () => {
    closePopover();
    setActiveControl('sources');
  };

  return (
    <Flex overflow={isMobileBreakpoint ? 'visible' : 'hidden'}>
      <Popover
        gutter={8}
        initialFocusRef={initialFocusRef}
        isLazy
        isOpen={isOpen}
        modifiers={availableHeightModifiers}
        placement="bottom-end"
        returnFocusOnClose={false}
        onClose={closePopover}
      >
        {hasAppliedFilters && !isMobileBreakpoint ? (
          <PopoverAnchor>
            <ActiveFiltersPill
              categories={filteredCategories}
              labels={filteredLabels}
              sources={filteredSources}
              whoLabels={filteredWhoLabels}
              onClear={clearFilters}
              onClick={togglePopover}
            />
          </PopoverAnchor>
        ) : isMobileBreakpoint ? (
          <PopoverTrigger>
            <Button
              aria-label="Filter"
              isActive={isOpen}
              pos="relative"
              px="0"
              size="ah"
              variant="ahSecondary"
              w="var(--chakra-sizes-10)"
              onClick={openPopover}
            >
              {filtersCount > 0 && <CountBadge>{filtersCount}</CountBadge>}
              <Icon icon="Filter" size={4} />
            </Button>
          </PopoverTrigger>
        ) : (
          <PopoverTrigger>
            <Button
              isActive={isOpen}
              leftIcon={<Icon icon="Filter" size={4} />}
              size="ah"
              variant="ahSecondary"
              onClick={openPopover}
            >
              Filter
            </Button>
          </PopoverTrigger>
        )}

        <Portal>
          <PopoverContent
            bg="white"
            borderColor="line.DEFAULT"
            borderRadius="ah-xl"
            boxShadow="0 4px 24px rgba(0,0,0,0.10)"
            display="flex"
            flexDirection="column"
            // Held to the room popper measured for it, so a planner with more
            // filters than fit the screen scrolls the list instead of running
            // off the bottom. The fallback only covers the frame before that
            // measurement lands. `hidden` keeps the scroll area inside the
            // rounded corners.
            maxH={`var(${AVAILABLE_HEIGHT_VAR}, 70dvh)`}
            overflow="hidden"
            w={{ base: '350px', md: '450px' }}
            _focusVisible={{
              outline: 0,
              boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
            }}
          >
            <PopoverBody
              display="flex"
              flexDirection="column"
              minH="0"
              overflow="hidden"
              p="2"
            >
              <Tabs
                display="flex"
                flexDirection="column"
                isFitted
                minH="0"
                overflow="hidden"
                variant="unstyled"
              >
                <FilterTabList>
                  <FilterTab icon="Palette">Categories</FilterTab>
                  <FilterTab icon="User">Who</FilterTab>
                  <FilterTab icon="Tag">Labels</FilterTab>
                  <FilterTab icon="Zap">Sources</FilterTab>
                </FilterTabList>

                {/* The rail stays put and the list under it scrolls, so the
                    tabs are still reachable on an experience with more filters
                    than fit the screen. `contain` keeps a touch drag that runs
                    off the end of the list from scrolling the page behind. */}
                <TabPanels
                  flex="1"
                  minH="0"
                  overflowY="auto"
                  overscrollBehavior="contain"
                >
                  <FilterTabPanel>
                    {experience.categories.length === 0 ? (
                      <FilterEmptyState>No categories yet</FilterEmptyState>
                    ) : (
                      <VStack alignItems="flex-start" spacing={2}>
                        {experience.categories.map((category, index) => (
                          <CategoryFilterRow
                            category={category}
                            key={category.id}
                            ref={index === 0 ? initialFocusRef : undefined}
                            isSelected={filterIds.categories.includes(
                              category.id
                            )}
                            onToggle={handleCategoryToggle}
                          />
                        ))}
                      </VStack>
                    )}
                    {!readOnly && (
                      <ManageLink onClick={manageCategoriesAndLabels} />
                    )}
                  </FilterTabPanel>

                  <FilterTabPanel>
                    {experience.whoLabels.length === 0 ? (
                      <FilterEmptyState>No who labels yet</FilterEmptyState>
                    ) : (
                      <FilterLabelList
                        labels={experience.whoLabels}
                        selectedIds={filterIds.whoLabels}
                        onToggle={handleWhoLabelToggle}
                      />
                    )}
                    {!readOnly && (
                      <ManageLink onClick={manageCategoriesAndLabels} />
                    )}
                  </FilterTabPanel>

                  <FilterTabPanel>
                    {experience.labels.length === 0 ? (
                      <FilterEmptyState>No labels yet</FilterEmptyState>
                    ) : (
                      <FilterLabelList
                        labels={experience.labels}
                        selectedIds={filterIds.labels}
                        onToggle={handleLabelToggle}
                      />
                    )}
                    {!readOnly && (
                      <ManageLink onClick={manageCategoriesAndLabels} />
                    )}
                  </FilterTabPanel>

                  <FilterTabPanel>
                    {activeFeeds.length === 0 ? (
                      <Flex
                        align="center"
                        color="ink.muted"
                        direction="column"
                        fontSize="ah-control"
                        gap="4"
                        justify="center"
                        py="4"
                      >
                        {/* Every configured source can be paused (access to it
                            revoked) at once. That is not "no sources yet". */}
                        {experience.feeds.length === 0
                          ? 'No sources yet'
                          : 'No sources are delivering events right now'}
                        {!readOnly && (
                          <Button
                            leftIcon={<Icon icon="Plus" size={4} />}
                            size="ah-sm"
                            variant="ahPrimary"
                            onClick={manageSources}
                          >
                            Manage sources
                          </Button>
                        )}
                      </Flex>
                    ) : (
                      <>
                        <VStack>
                          {activeFeeds.map((feed) => (
                            <SourceFilterRow
                              isSelected={filterIds.sources.includes(feed.id)}
                              key={feed.id}
                              source={feed}
                              onToggle={handleSourceToggle}
                            />
                          ))}
                        </VStack>
                        {/* The empty state already carries its own Manage sources
                            CTA, so the link only rides the populated list. */}
                        {!readOnly && <ManageLink onClick={manageSources} />}
                      </>
                    )}
                  </FilterTabPanel>
                </TabPanels>
              </Tabs>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Flex>
  );
};
